import type { BatchDetail, AlertLevel } from '../../api/types';

/**
 * Logique de roulement de l'affichage TV — pure et testable.
 *
 * Priorite (plan section 1.9) : les fournees en cours et celles en alerte
 * passent en premier, les epinglees d'abord. Puis pagination en pages de taille
 * fixe. La minuterie et le tampon anti-reflow sont geres par le composant.
 */

const SEVERITY_RANK: Record<AlertLevel | 'none', number> = { blocking: 3, warning: 2, info: 1, none: 0 };

const STATUS_RANK: Record<string, number> = {
  RUNNING: 3,
  LOADING: 3,
  UNLOADING: 3,
  PLANNED: 1,
  DONE: 0,
  CANCELLED: 0,
};

/** Une carte porte le detail de fournee et si elle est epinglee (planning). */
export interface RotationCard extends BatchDetail {
  pinned: boolean;
  t0: string;
}

/**
 * Trie les fournees pour l'affichage : epinglees, puis alertes les plus graves,
 * puis fournees en cours, puis par ordre chronologique.
 */
export function sortByPriority<T extends RotationCard>(cards: T[]): T[] {
  return [...cards].sort((a, b) => {
    if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
    const sev = SEVERITY_RANK[b.severity] - SEVERITY_RANK[a.severity];
    if (sev !== 0) return sev;
    const st = (STATUS_RANK[b.status] ?? 0) - (STATUS_RANK[a.status] ?? 0);
    if (st !== 0) return st;
    return new Date(a.t0).getTime() - new Date(b.t0).getTime();
  });
}

/**
 * Trie les fournees par date de debut (chronologique), pour que l'ordre des
 * cartes suive l'axe de la timeline (lien visuel plus clair). Departage par
 * numero de fournee.
 */
export function sortByStartDate<T extends { t0: string; number: string }>(cards: T[]): T[] {
  return [...cards].sort((a, b) => {
    const t = new Date(a.t0).getTime() - new Date(b.t0).getTime();
    return t !== 0 ? t : a.number.localeCompare(b.number);
  });
}

/** Decoupe une liste en pages de `pageSize` (>= 1). */
export function paginate<T>(items: T[], pageSize: number): T[][] {
  const size = Math.max(1, pageSize);
  if (items.length === 0) return [[]];
  const pages: T[][] = [];
  for (let i = 0; i < items.length; i += size) pages.push(items.slice(i, i + size));
  return pages;
}

/**
 * Repli automatique de la taille de page : si l'espace ne permet pas d'afficher
 * `desired` cartes, on reduit jusqu'a ce que ca tienne, sans descendre sous 2
 * (ni sous 1 si l'ecran est vraiment petit).
 */
export function fitPageSize(desired: number, availableWidth: number, minCardWidth: number): number {
  if (availableWidth <= 0) return desired;
  const fits = Math.floor(availableWidth / minCardWidth);
  return Math.max(1, Math.min(desired, fits || 1));
}
